import React, { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { useSnackbar } from "notistack";
import RsModal from "../../../../Components/UI/ma-modal";
import { IoClose } from "react-icons/io5";
import { IoIosCheckmarkCircleOutline } from "react-icons/io";
import MaButton from "../../../../Components/UI/ma-button";
import Loader from "../../../../Components/Common/Loader";
import { AddProductStepper } from "./add-product-stepper";
import { AddProductStepMedia } from "./add-product-step-media";
import { AddProductStepBasic } from "./add-product-step-basic";
import { AddProductStepPrice } from "./add-product-step-price";
import { GET_CATEGORIES_N_SUB, GET_PRODUCT_ORIGIN } from "../../../../Utils/Queries";
import { CREATE_PRODUCT_V3 } from "../../../../Utils/Mutations";
import {
  uploadFileToCloudinary,
  uploadVideoToCloudinary,
} from "../../../../Utils/api";

const STEPS = [
  { title: "Media", subtitle: "Images & Videos" },
  { title: "Basic Info", subtitle: "Name, price, category" },
  { title: "Price & Inventory", subtitle: "Amount, Stock" },
];

function initialFormState() {
  return {
    productName: "",
    categoryId: "",
    productOriginId: "",
    description: "",
    tags: [],
    price: "",
    stock: "",
    lowStockAlert: "",
    weight: "",
    deliveryTime: "",
  };
}

function genId() {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function parseEmbedVideoUrls(raw) {
  if (!raw || typeof raw !== "string") return [];
  return raw
    .split(/[\n,]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

export default function AddProductModal({ isOpen, onClose, onProductCreated }) {
  const { enqueueSnackbar } = useSnackbar();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(initialFormState);
  const [imageItems, setImageItems] = useState([]);
  const [videoItems, setVideoItems] = useState([]);
  const [embedVideoUrls, setEmbedVideoUrls] = useState("");
  const [stepError, setStepError] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);

  const { data: catData, loading: categoriesLoading } =
    useQuery(GET_CATEGORIES_N_SUB);
  const { data: originData, loading: originsLoading } =
    useQuery(GET_PRODUCT_ORIGIN);

  const [createProductV3] = useMutation(CREATE_PRODUCT_V3);

  const categoryOptions = useMemo(() => {
    const rows = catData?.fetchAllCategoriesAndTheirSubCategories;
    if (!Array.isArray(rows)) return [];
    return rows
      .map((row) => row?.category)
      .filter((c) => c != null && c._id != null && c.isActive !== false);
  }, [catData]);
  const originOptions = useMemo(
    () => originData?.getProductOrigins?.items ?? [],
    [originData]
  );

  useEffect(() => {
    if (!isOpen) return;
    setStep(0);
    setForm(initialFormState());
    setImageItems((prev) => {
      prev.forEach((i) => {
        if (i.preview) URL.revokeObjectURL(i.preview);
      });
      return [];
    });
    setVideoItems([]);
    setEmbedVideoUrls("");
    setStepError("");
    setSubmitError("");
    setIsSubmitting(false);
    setSuccessOpen(false);
  }, [isOpen]);

  useEffect(() => {
    if (!successOpen) return undefined;
    const timeout = window.setTimeout(() => {
      setSuccessOpen(false);
      onClose?.();
    }, 1300);
    return () => window.clearTimeout(timeout);
  }, [successOpen, onClose]);

  const handleClose = () => {
    if (isSubmitting || successOpen) return;
    onClose?.();
  };

  const onAddImages = (files, max) => {
    setImageItems((prev) => {
      const remaining = max - prev.length;
      if (remaining <= 0) return prev;
      const take = files.slice(0, remaining);
      const next = take.map((file) => ({
        id: genId(),
        file,
        preview: URL.createObjectURL(file),
      }));
      return [...prev, ...next];
    });
  };

  const onRemoveImage = (id) => {
    setImageItems((prev) => {
      const item = prev.find((i) => i.id === id);
      if (item?.preview) URL.revokeObjectURL(item.preview);
      return prev.filter((i) => i.id !== id);
    });
  };

  const onAddVideos = (files, max) => {
    setVideoItems((prev) => {
      const remaining = max - prev.length;
      if (remaining <= 0) return prev;
      const take = files.slice(0, remaining);
      return [
        ...prev,
        ...take.map((file) => ({
          id: genId(),
          file,
        })),
      ];
    });
  };

  const onRemoveVideo = (id) => {
    setVideoItems((prev) => prev.filter((i) => i.id !== id));
  };

  const validateStep = (s) => {
    if (s === 0) {
      if (imageItems.length === 0) {
        setStepError(
          "To go to the next step, add at least one product image:\n• Click the upload area (or use your file picker) to choose JPEG / PNG / WebP files.\n• You can add up to 5 images; the first one is used as the cover."
        );
        return false;
      }
    }
    if (s === 1) {
      const missing = [];
      if (!form.productName.trim()) {
        missing.push("Enter a product name (the field at the top of this step).");
      }
      if (!form.categoryId) {
        missing.push("Choose a category from the Category dropdown.");
      }
      if (!form.productOriginId) {
        missing.push("Choose a product origin from the Product Origin dropdown.");
      }
      if (!form.description.trim()) {
        missing.push("Write a description in the Description box.");
      }
      if (missing.length) {
        setStepError(
          `Fill in the following, then click Next again:\n• ${missing.join("\n• ")}`
        );
        return false;
      }
    }
    return true;
  };

  const validatePriceStep = () => {
    const issues = [];
    const priceRaw = form.price.trim();
    const stockRaw = form.stock.trim();
    const lowRaw = form.lowStockAlert.trim();
    const weightRaw = form.weight.trim();
    const deliveryRaw = form.deliveryTime.trim();
    const delivery = parseFloat(deliveryRaw);

    const price = parseFloat(priceRaw);
    const stock = parseInt(stockRaw, 10);
    const low = parseInt(lowRaw, 10);
    const weight = parseFloat(weightRaw);

    if (!priceRaw || !Number.isFinite(price) || price <= 0) {
      issues.push("Enter a valid Price ($) — a number greater than 0.");
    }
    if (!stockRaw || !Number.isFinite(stock) || stock < 0) {
      issues.push("Enter Stock quantity — a whole number (0 or more).");
    }
    if (!lowRaw || !Number.isFinite(low) || low < 0) {
      issues.push("Enter Low stock alert — a whole number (0 or more).");
    }
    if (!weightRaw || !Number.isFinite(weight) || weight < 0) {
      issues.push("Enter Weight (kg) — a number (0 or more).");
    }
    if (!deliveryRaw || !Number.isFinite(delivery) || delivery <= 0) {
      issues.push(
        "Enter Delivery processing time as a number (e.g. 2 for 2 days, or 1.5 for 1.5 days)."
      );
    }

    if (issues.length) {
      setSubmitError(
        `Fix the following, then click “Add Product” again:\n• ${issues.join("\n• ")}`
      );
      return false;
    }
    return true;
  };

  const submitProduct = async () => {
    setSubmitError("");
    if (!validatePriceStep()) return;

    setIsSubmitting(true);
    const imageUrls = [];
    const videoUrls = [];

    try {
      for (let i = 0; i < imageItems.length; i++) {
        const file = imageItems[i].file;
        try {
          const response = await uploadFileToCloudinary(file, () => {});
          imageUrls.push(response.data.secure_url);
        } catch (err) {
          console.error(err);
          enqueueSnackbar(
            "Image upload failed. Check file size (max ~5MB) and try again.",
            { variant: "error" }
          );
          setIsSubmitting(false);
          return;
        }
      }

      for (let i = 0; i < videoItems.length; i++) {
        const file = videoItems[i].file;
        try {
          const response = await uploadVideoToCloudinary(file, () => {});
          videoUrls.push(response.data.secure_url);
        } catch (err) {
          console.error(err);
          enqueueSnackbar(
            "Video upload failed. Try a smaller file or a supported format.",
            { variant: "error" }
          );
          setIsSubmitting(false);
          return;
        }
      }

      const embedUrls = parseEmbedVideoUrls(embedVideoUrls);
      const allVideoUrls = [...videoUrls, ...embedUrls];

      const payload = {
        name: form.productName.trim(),
        description: form.description.trim(),
        categoryId: form.categoryId,
        productOriginId: form.productOriginId,
        price: parseFloat(form.price),
        quantityInStock: parseInt(form.stock, 10),
        imageUrls,
        videoUrls: allVideoUrls,
        tags: [...form.tags],
        weight: parseFloat(form.weight),
        deliveryProcessingTime: parseFloat(form.deliveryTime.trim()),
        lowStockAlert: parseInt(form.lowStockAlert, 10),
      };

      await createProductV3({ variables: { payload } });

      onProductCreated?.();
      setSuccessOpen(true);
    } catch (e) {
      const msg =
        e?.graphQLErrors?.[0]?.message ||
        e?.networkError?.message ||
        e?.message ||
        "Could not create product.";
      setSubmitError(msg);
      enqueueSnackbar(msg, { variant: "error" });
    } finally {
      setIsSubmitting(false);
    }
  };

  const goNext = () => {
    setStepError("");
    setSubmitError("");
    if (!validateStep(step)) return;
    if (step < STEPS.length - 1) {
      setStep((s) => s + 1);
      return;
    }
    submitProduct();
  };

  const goBack = () => {
    setStepError("");
    setSubmitError("");
    setStep((s) => Math.max(0, s - 1));
  };

  const isLastStep = step === STEPS.length - 1;

  return (
    <RsModal isOpen={isOpen} onClose={handleClose}>
      <div className="relative flex h-[min(100dvh,900px)] max-h-[92dvh] w-[calc(100vw-24px)] max-w-[960px] flex-col overflow-hidden rounded-2xl bg-white shadow-2xl outline-none sm:h-auto sm:max-h-[min(720px,90vh)] sm:w-[min(960px,calc(100vw-32px))] sm:rounded-xl">
        {successOpen ? (
          <div className="fixed left-1/2 top-6 z-[1500] flex w-[min(504px,calc(100vw-32px))] -translate-x-1/2 items-center justify-between gap-4 rounded-[16px] bg-[#282828] px-7 py-2 shadow-[0_18px_45px_rgba(0,0,0,0.24)] sm:px-8">
            <div className="flex items-center gap-5">
              <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full border-2 border-[#00A76F] text-[#00A76F]">
                <IoIosCheckmarkCircleOutline className="text-[22px]" />
              </span>
              <span className="text-[13px] font-medium text-white sm:text-[15px]">
                Product submitted for admin review
              </span>
            </div>
            <button
              type="button"
              onClick={() => {
                setSuccessOpen(false);
                onClose?.();
              }}
              className="grid h-10 w-10 shrink-0 cursor-pointer place-items-center rounded-full border-0 bg-transparent text-[#8B93A1] transition-colors hover:text-white"
              aria-label="Dismiss success message"
            >
              <IoClose className="text-[42px]" />
            </button>
          </div>
        ) : null}

        {isSubmitting ? (
          <div className="absolute inset-0 z-[60] flex flex-col items-center justify-center gap-3 bg-white/85">
            <Loader />
            <p className="my-0 text-[13px] text-[#6B7280]">
              Uploading media and creating product…
            </p>
          </div>
        ) : null}

        <div
          style={{ borderBottom: "1px solid #EEF0F3" }}
          className="flex shrink-0 items-center justify-between border-b border-[#EEF0F3] px-4 py-3 sm:px-5 sm:py-4"
        >
          <h2
            id="spring-modal-title"
            className="my-0 text-[16px] font-semibold text-[#272727]"
          >
            Add Product
          </h2>
          <button
            type="button"
            onClick={handleClose}
            disabled={isSubmitting}
            className="flex h-9 w-9 shrink-0 cursor-pointer items-center justify-center rounded-lg bg-[#EEF0F3] text-[#4B5563] transition-colors hover:bg-[#E5E7EB] disabled:opacity-50"
            aria-label="Close"
          >
            <IoClose className="text-[22px] text-[#DC2626]" />
          </button>
        </div>

        <div className="shrink-0 border-b border-[#EEF0F3] bg-[#F8F8F8] md:hidden">
          <AddProductStepper
            steps={STEPS}
            activeIndex={step}
            variant="horizontal"
          />
        </div>

        <div className="flex min-h-0 flex-1 flex-col md:flex-row">
          <aside
            style={{ borderRight: "1px solid #EAEAEA" }}
            className="hidden shrink-0 border-b border-[#EEF0F3] bg-[#F8F8F8] md:block md:w-[220px] md:border-b-0"
          >
            <AddProductStepper steps={STEPS} activeIndex={step} />
          </aside>

          <div className="min-h-0 flex-1 overflow-y-auto p-4 md:p-0 md:py-5">
            <div className="overflow-hidden rounded-xl border border-[#EFEFEF] bg-white md:border-0 md:rounded-none">
            {step === 0 && (
              <AddProductStepMedia
                imageItems={imageItems}
                onAddImages={onAddImages}
                onRemoveImage={onRemoveImage}
                videoItems={videoItems}
                onAddVideos={onAddVideos}
                onRemoveVideo={onRemoveVideo}
                embedVideoUrls={embedVideoUrls}
                onEmbedVideoUrlsChange={setEmbedVideoUrls}
              />
            )}
            {step === 1 && (
              <AddProductStepBasic
                productName={form.productName}
                onProductNameChange={(v) =>
                  setForm((f) => ({ ...f, productName: v }))
                }
                categoryId={form.categoryId}
                onCategoryIdChange={(id) =>
                  setForm((f) => ({ ...f, categoryId: id }))
                }
                categoryOptions={categoryOptions}
                categoriesLoading={categoriesLoading}
                productOriginId={form.productOriginId}
                onProductOriginIdChange={(id) =>
                  setForm((f) => ({ ...f, productOriginId: id }))
                }
                originOptions={originOptions}
                originsLoading={originsLoading}
                description={form.description}
                onDescriptionChange={(v) =>
                  setForm((f) => ({ ...f, description: v }))
                }
                tags={form.tags}
                onTagsChange={(next) => setForm((f) => ({ ...f, tags: next }))}
              />
            )}
            {step === 2 && (
              <AddProductStepPrice
                price={form.price}
                onPriceChange={(v) => setForm((f) => ({ ...f, price: v }))}
                stock={form.stock}
                onStockChange={(v) => setForm((f) => ({ ...f, stock: v }))}
                lowStockAlert={form.lowStockAlert}
                onLowStockAlertChange={(v) =>
                  setForm((f) => ({ ...f, lowStockAlert: v }))
                }
                weight={form.weight}
                onWeightChange={(v) => setForm((f) => ({ ...f, weight: v }))}
                deliveryTime={form.deliveryTime}
                onDeliveryTimeChange={(v) =>
                  setForm((f) => ({ ...f, deliveryTime: v }))
                }
              />
            )}
            </div>
          </div>
        </div>

        {(stepError || (step === 2 && submitError)) && (
          <div
            role="alert"
            className="shrink-0 border-t border-[#FECACA] bg-[#FEF2F2] px-4 py-3 sm:px-5 md:px-8"
          >
            <p className="my-0 text-[11px] font-semibold uppercase tracking-wide text-[#991B1B]">
              {step === 2 && submitError
                ? "Before you can add this product"
                : "Before you can go to the next step"}
            </p>
            <p className="my-0 mt-1.5 text-[11px] leading-relaxed text-[#7F1D1D] whitespace-pre-line">
              {step === 2 && submitError ? submitError : stepError}
            </p>
          </div>
        )}

        <div
          style={{ borderTop: "1px solid #EEF0F3" }}
          className="flex shrink-0 flex-col gap-3 px-4 py-4 sm:px-5 md:flex-row md:items-center md:justify-between"
        >
          <p className="my-0 text-center text-[11px] text-[#9B9895] md:text-left">
            Fields marked <span className="text-red-500">*</span> are required
          </p>
          <div className="flex w-full gap-3 md:w-auto md:justify-end">
            <div
              className={`min-w-0 flex-1 [&>div]:w-full [&_button]:!w-full ${step === 0 ? "md:hidden" : "md:flex-none"}`}
            >
              <MaButton
                title="Back"
                onClick={goBack}
                disabled={isSubmitting || step === 0}
                sx={{
                  bgcolor: "#F8F8F8",
                  color: "#1F2937",
                  border: "none",
                  boxShadow: "none",
                  minHeight: "48px",
                  width: "100%",
                  borderRadius: "999px",
                  fontWeight: 600,
                  "&:hover": { bgcolor: "#EEF0F3", boxShadow: "none" },
                  "&.Mui-disabled": {
                    bgcolor: "#F8F8F8",
                    color: "#9CA3AF",
                  },
                  "@media (min-width: 768px)": {
                    minWidth: "120px",
                  },
                }}
              />
            </div>
            <div className="min-w-0 flex-1 md:flex-none [&>div]:w-full [&_button]:!w-full">
              <MaButton
                variant="primary"
                title={isLastStep ? "Add Product" : "Next"}
                onClick={goNext}
                disabled={isSubmitting}
                sx={{
                  minHeight: "48px",
                  width: "100%",
                  borderRadius: "999px",
                  fontWeight: 600,
                  "@media (min-width: 768px)": {
                    minWidth: "120px",
                  },
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </RsModal>
  );
}
